import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/cn';
import { Download, Radio, Loader2, Check, AlertTriangle, X, Clock, MapPin } from 'lucide-react';
import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import type { Incident } from '@/types';

/**
 * DownloadCADButton — pull-style CAD fetch.
 *
 * The cad-listener edge function is the PUSH path (Tyler / Spillman /
 * I/CAD post to us on dispatch). This button is the PULL path for
 * when the crew wants to go grab a dispatched run that they can see
 * on the MDT but we didn't auto-capture — either because the webhook
 * failed, EMSPCR was offline when it fired, or the crew opened the
 * ePCR before the CAD event reached our server.
 *
 * UX: click → modal with the last 25 "available" CAD events for this
 * agency (records that came through cad-listener but aren't linked to
 * an incident yet, plus recent incidents with cad_number set). One
 * tap on a row pulls the run's dispatch data into the current
 * incident record (address / GPS / incident types / dispatch time /
 * cad_number / run_number).
 *
 * When a Tyler / Spillman pull API credential is configured on the
 * agency, this component will also fetch live data. Until then, the
 * "source list" is derived from the PSAP records already in our own
 * DB that match the crew's agency and dispatch window.
 */

interface DispatchRow {
  cad_number: string | null;
  run_number: string | null;
  dispatch_time: string | null;
  address: string | null;
  city: string | null;
  incident_types: string[];
  gps_lat: number | null;
  gps_lng: number | null;
  /** Source id — either an existing incident ID or a stub CAD event. */
  source_id: string;
  source_kind: 'existing_incident' | 'cad_event';
}

export function DownloadCADButton({ incidentId }: { incidentId: string }) {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DispatchRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);

  async function load() {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // Until a vendor-specific pull integration is configured, we surface
      // the recent CAD-originated incidents already in our own DB: the
      // last 6 hours of dispatches for this agency that the crew might
      // want to transplant onto a new incident record.
      const since = new Date(Date.now() - 6 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from('incidents')
        .select('id, cad_number, run_number, dispatch_time, address, city, incident_types, gps_lat, gps_lng')
        .eq('agency_id', user.agency_id)
        .gte('dispatch_time', since)
        .order('dispatch_time', { ascending: false })
        .limit(25);
      if (error) throw error;
      const list: DispatchRow[] = (data ?? []).map((r) => ({
        cad_number: r.cad_number,
        run_number: r.run_number,
        dispatch_time: r.dispatch_time,
        address: r.address,
        city: r.city,
        incident_types: r.incident_types ?? [],
        gps_lat: r.gps_lat,
        gps_lng: r.gps_lng,
        source_id: r.id,
        source_kind: 'existing_incident',
      }));
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function applyToCurrent(row: DispatchRow) {
    setApplying(row.source_id);
    setError(null);
    try {
      const incident = await db.incidents.get(incidentId);
      if (!incident) throw new Error('Incident not loaded');
      const now = new Date().toISOString();
      const patch: Partial<Incident> = {
        cad_number: row.cad_number ?? incident.cad_number,
        run_number: row.run_number ?? incident.run_number,
        dispatch_time: row.dispatch_time ?? incident.dispatch_time,
        address: row.address ?? incident.address,
        city: row.city ?? incident.city,
        incident_types: (row.incident_types.length ? row.incident_types : incident.incident_types) as Incident['incident_types'],
        gps_lat: row.gps_lat ?? incident.gps_lat,
        gps_lng: row.gps_lng ?? incident.gps_lng,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await db.incidents.update(incidentId, {
        ...patch,
        _dirty: true,
        _last_modified: now,
      } as any);
      await enqueueSync('incidents', incidentId, 'update', {
        id: incidentId, ...patch, updated_at: now,
      });
      setAppliedAt(row.source_id);
      window.setTimeout(() => setOpen(false), 700);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(null);
    }
  }

  return (
    <>
      <button
        onClick={() => { setOpen(true); void load(); }}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:border-blue-600 rounded min-h-touch whitespace-nowrap"
        title="Pull dispatched runs from CAD and apply to this incident — can be re-run any time to refresh"
      >
        <Radio size={14} className="text-blue-400" />
        <span>Import CAD</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3">
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-950 px-3 py-1.5">
              <div className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest text-slate-200">
                <Radio size={13} className="text-blue-400" />
                Dispatched Runs · Last 6 hours
              </div>
              <button onClick={() => setOpen(false)} className="h-7 w-7 flex items-center justify-center text-slate-400 hover:bg-slate-800">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                  <Loader2 size={16} className="animate-spin" /> Loading dispatches…
                </div>
              ) : error ? (
                <div className="p-4 text-sm text-red-300 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5" /> {error}
                </div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  No recent dispatches found. If CAD push isn't firing, confirm the
                  agency's cad-listener webhook is reaching <code className="font-mono">/functions/v1/cad-listener</code>.
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {rows.map((r) => {
                    const busy = applying === r.source_id;
                    const done = appliedAt === r.source_id;
                    return (
                      <button
                        key={r.source_id}
                        onClick={() => void applyToCurrent(r)}
                        disabled={busy || done}
                        className={cn(
                          'w-full flex flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-slate-800 transition-colors',
                          done && 'bg-emerald-950/30',
                        )}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="font-mono text-[11px] tabular-nums text-slate-500 shrink-0">
                            <Clock size={10} className="inline mr-0.5" />
                            {r.dispatch_time ? new Date(r.dispatch_time).toLocaleTimeString([], { hour12: false }) : '—'}
                          </span>
                          <span className="font-mono text-xs text-blue-300 shrink-0">
                            {r.cad_number ?? r.run_number ?? '—'}
                          </span>
                          <span className="flex-1 text-sm text-slate-100 truncate">
                            {(r.incident_types ?? []).join(' · ') || 'Unspecified'}
                          </span>
                          {busy && <Loader2 size={12} className="animate-spin text-blue-400 shrink-0" />}
                          {done && <Check size={14} className="text-emerald-400 shrink-0" />}
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-slate-400">
                          <MapPin size={10} />
                          <span className="truncate">
                            {[r.address, r.city].filter(Boolean).join(', ') || '(no address)'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-slate-700 bg-slate-900 px-3 py-1.5 text-[10px] font-mono text-slate-500">
              Tap a row to apply its CAD data to this incident. Manual edits on the current record are preserved unless the CAD row has a non-null value for the same field.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
